export * from '~/sites/shop/store/auth'

import { errorFromResponse } from '~/app/notifications'
import { actions as parentActions } from '~/sites/shop/store/auth'
import jsCookie from 'js-cookie'

const guestCartBackendCookie = 'X-Inspishop-Guest-Cart'
const guestCartStorageCookie = 'X-Inspishop-Guest-Cart-Token'
const guestCartStorageKey = 'inspishop.guestCartToken'
const guestCartAxiosHeader = 'x-inspishop-guest-cart'

function isCartDebugEnabled(app) {
  if (!process.client) {
    return false
  }

  try {
    if (window.localStorage.getItem('inspishop.cartDebug') === '1') {
      return true
    }
  } catch (e) {
    // Ignore storage availability issues; localhost logging below still works.
  }

  return ['localhost', '127.0.0.1'].includes(window.location.hostname)
}

function debugAuthCart(app, step, payload = {}) {
  if (!isCartDebugEnabled(app)) {
    return
  }

  console.info('[cart-debug][auth]', step, {
    routeName: app?.context?.route?.name,
    routePath: app?.context?.route?.path,
    timestamp: new Date().toISOString(),
    ...payload,
  })
}

export function getLoginRedirectPath(app, redirect) {
  return redirect ? redirect : app.localePath({ name: 'user' })
}

export function getLogoutRedirectPath(app, redirect) {
  return redirect ? redirect : app.localePath({ name: 'index' })
}

export function refreshCartAfterAuth(dispatch) {
  return dispatch('cart/FETCH_CART', { forceReload: true }, { root: true }).catch(e => {
    console.error(e)
  })
}

function getClientCookie(name) {
  if (!process.client) {
    return null
  }

  return jsCookie.get(name)
}

function getClientStorage(name) {
  if (!process.client) {
    return null
  }

  try {
    return window.localStorage.getItem(name)
  } catch (e) {
    return null
  }
}

function getGuestCartToken(rootState) {
  return (
    rootState?.cart?.guestCartToken ||
    getClientCookie(guestCartStorageCookie) ||
    getClientStorage(guestCartStorageKey) ||
    getClientCookie(guestCartBackendCookie) ||
    null
  )
}

function getLoginRequestConfig(rootState) {
  const guestCartToken = getGuestCartToken(rootState)

  if (!guestCartToken) {
    return undefined
  }

  return {
    headers: {
      [guestCartAxiosHeader]: guestCartToken,
    },
  }
}

export const actions = {
  ...parentActions,
  async LOGIN({ dispatch, commit, rootState }, options) {
    try {
      const { email, password, redirect, token } = options || {}
      debugAuthCart(this.app, 'LOGIN start', {
        hasTokenLogin: !!token,
        hasRedirect: !!redirect,
        hasGuestCartToken: !!getGuestCartToken(rootState),
      })
      if (token) {
        await dispatch('SET_AUTH', token)
      } else {
        const request = await this.$axios.$post(
          this.$env.STORE_URL + '/api/oauth/login',
          {
            email,
            password,
          },
          getLoginRequestConfig(rootState),
        )
        await dispatch('SET_AUTH', request.data.access_token)
      }
      await dispatch('SET_AXIOS_TOKEN')
      await dispatch('user/FETCH_USER', { forceReload: true }, { root: true })
      if (await dispatch('RESTRICTED_GROUP')) {
        return false
      }
      commit(
        'globals/ADD_FLASH_MESSAGE',
        { type: 'success', text: this.app.i18n.t('globals.login.successNotificationText') },
        { root: true },
      )
      await dispatch('cart/CLEAR_GUEST_CART_TOKEN', {}, { root: true })
      debugAuthCart(this.app, 'LOGIN before redirect', {
        redirectPath: getLoginRedirectPath(this.app, redirect),
      })
      refreshCartAfterAuth(dispatch)
      window.location.assign(getLoginRedirectPath(this.app, redirect))
      return true
    } catch (e) {
      debugAuthCart(this.app, 'LOGIN failed', {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      })
      errorFromResponse(e.response)
      return false
    }
  },
  async LOGOUT({ dispatch }, options) {
    try {
      const { redirect } = options || {}
      debugAuthCart(this.app, 'LOGOUT start', {
        redirect,
      })
      await this.$axios.$post(this.$env.STORE_URL + '/api/oauth/logout')
      await dispatch('REMOVE_AUTH')
      await dispatch('SET_AXIOS_TOKEN')
      await dispatch('SET_VARIANT')
      await dispatch('SET_USER_GROUPS')
      await dispatch('user/RESET_USER', {}, { root: true })
      await dispatch('cart/CLEAR_GUEST_CART_TOKEN', {}, { root: true })
      debugAuthCart(this.app, 'LOGOUT after auth cleanup', {
        redirectPath: getLogoutRedirectPath(this.app, redirect),
      })
      if (redirect !== false) {
        refreshCartAfterAuth(dispatch)
        window.location.assign(getLogoutRedirectPath(this.app, redirect))
      } else {
        await refreshCartAfterAuth(dispatch)
      }
    } catch (e) {
      debugAuthCart(this.app, 'LOGOUT failed', {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      })
      errorFromResponse(e.response)
    }
  },
}
